import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ROLE_LABELS } from "@/lib/constants";
import { Plus, Trash2, Save, GripVertical, X, Workflow } from "lucide-react";

const ROLES = ["attorney", "general_counsel", "compliance", "legal_manager", "contract_analyst", "records_management"];
const OPS = [
  { id: "eq", label: "equals" }, { id: "ne", label: "not equals" },
  { id: "gt", label: ">" }, { id: "gte", label: "≥" },
  { id: "lt", label: "<" }, { id: "lte", label: "≤" },
  { id: "in", label: "in list" },
];
const FIELDS = [
  { id: "contract_amount", label: "Contract amount" },
  { id: "contract_type", label: "Contract type" },
  { id: "department", label: "Department" },
  { id: "baa_required", label: "BAA required" },
  { id: "handles_phi_pii_pci", label: "PHI/PII/PCI" },
  { id: "board_approval_required", label: "Board approval required" },
  { id: "is_budgeted", label: "Budgeted" },
];

const empty = {
  name: "", description: "", priority: 10,
  rules: [], steps: [{ order: 1, role: "attorney", name: "Legal Review", sla_hours: 48 }],
  active: true,
};

export default function WorkflowsAdmin() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/workflows"); setRows(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name || !editing.steps.length) return toast.error("Name + at least one step required");
    try {
      const body = { ...editing };
      delete body.id; delete body.created_at; delete body.created_by; delete body._id; delete body.source;
      if (editing.id) await api.patch(`/workflows/${editing.id}`, body);
      else await api.post("/workflows", body);
      toast.success("Workflow saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (w) => {
    if (!window.confirm(`Delete workflow "${w.name}"?`)) return;
    try { await api.delete(`/workflows/${w.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6" data-testid="workflows-admin-page">
      <div className="mb-6 flex items-start">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Phase 7 · Approval Workflows</div>
          <h1 className="text-2xl font-bold text-slate-900">Workflow Engine</h1>
          <p className="text-sm text-slate-500 mt-1">Rule-driven approval chains. Highest-priority active template whose rules match a contract wins.</p>
        </div>
        <button data-testid="wf-new-btn" onClick={() => setEditing({ ...empty })}
                className="ml-auto h-10 px-4 rounded-md bg-[#005289] text-white text-sm font-semibold flex items-center gap-2">
          <Plus className="w-4 h-4"/> New Workflow
        </button>
      </div>

      <div className="grid gap-3" data-testid="wf-list">
        {rows.map(w => (
          <div key={w.id} className="bg-white border border-slate-200 rounded-md p-4">
            <div className="flex items-start gap-3">
              <Workflow className="w-5 h-5 text-[#005289] mt-0.5"/>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <div className="font-bold text-slate-900">{w.name}</div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${w.active ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-500"}`}>{w.active ? "Active" : "Inactive"}</span>
                  <span className="text-[10px] text-slate-500">priority {w.priority}</span>
                  <span className="ml-auto text-[10px] font-mono text-slate-400">{w.key}</span>
                </div>
                {w.description && <div className="text-xs text-slate-600 mt-1">{w.description}</div>}
                <div className="mt-2 flex flex-wrap gap-1">
                  {(w.rules || []).length === 0 && <span className="text-[10px] text-slate-400">catch-all (no rules)</span>}
                  {(w.rules || []).map((r, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-mono">
                      {r.field} {OPS.find(o => o.id === r.op)?.label || r.op} {String(r.value)}
                    </span>
                  ))}
                </div>
                <div className="mt-2 flex items-center gap-1 text-xs text-slate-600">
                  {(w.steps || []).map((s, i) => (
                    <span key={i} className="flex items-center gap-1">
                      <span className="px-2 py-0.5 bg-[#005289] text-white rounded">{i + 1}. {ROLE_LABELS[s.role] || s.role}: {s.name}</span>
                      {i < (w.steps.length - 1) && <span className="text-slate-300">→</span>}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <button data-testid={`wf-edit-${w.id}`} onClick={() => setEditing(w)} className="text-[#005289] hover:underline text-xs">Edit</button>
                <button data-testid={`wf-del-${w.id}`} onClick={() => del(w)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>
              </div>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="text-center py-8 text-slate-400 text-sm">No workflows yet.</div>}
      </div>

      {editing && <WorkflowModal w={editing} setW={setEditing} onSave={save} onClose={() => setEditing(null)}/>}
    </div>
  );
}

function WorkflowModal({ w, setW, onSave, onClose }) {
  const addRule = () => setW({ ...w, rules: [...(w.rules || []), { field: "contract_amount", op: "gt", value: 0 }] });
  const setRule = (i, patch) => setW({ ...w, rules: w.rules.map((r, j) => j === i ? { ...r, ...patch } : r) });
  const delRule = (i) => setW({ ...w, rules: w.rules.filter((_, j) => j !== i) });
  const addStep = () => setW({ ...w, steps: [...w.steps, { order: w.steps.length + 1, role: "attorney", name: "Review", sla_hours: 24 }] });
  const setStep = (i, patch) => setW({ ...w, steps: w.steps.map((s, j) => j === i ? { ...s, ...patch } : s) });
  const delStep = (i) => setW({ ...w, steps: w.steps.filter((_, j) => j !== i).map((s, k) => ({ ...s, order: k + 1 })) });
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-6 z-50" data-testid="wf-modal">
      <div className="bg-white rounded-md w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white">
          <div className="font-bold">{w.id ? "Edit Workflow" : "New Workflow"}</div>
          <button onClick={onClose}><X className="w-4 h-4 text-slate-400"/></button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name *"><input data-testid="wf-name" className={inp} value={w.name} onChange={e => setW({ ...w, name: e.target.value })}/></Field>
            <Field label="Priority (higher = wins)"><input type="number" className={inp} value={w.priority} onChange={e => setW({ ...w, priority: parseInt(e.target.value) || 0 })}/></Field>
          </div>
          <Field label="Description"><textarea rows={2} className={inp} value={w.description} onChange={e => setW({ ...w, description: e.target.value })}/></Field>
          <label className="flex items-center gap-2 text-xs"><input data-testid="wf-active" type="checkbox" checked={w.active} onChange={e => setW({ ...w, active: e.target.checked })}/> Active</label>

          <div className="border border-slate-200 rounded-md p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Match Rules (all must be true)</div>
              <button data-testid="wf-add-rule" onClick={addRule} className="ml-auto text-xs text-[#005289] hover:underline">+ Add rule</button>
            </div>
            {(w.rules || []).map((r, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <select value={r.field} onChange={e => setRule(i, { field: e.target.value })} className="h-8 px-2 border border-slate-200 rounded text-xs flex-1">
                  {FIELDS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
                <select value={r.op} onChange={e => setRule(i, { op: e.target.value })} className="h-8 px-2 border border-slate-200 rounded text-xs w-28">
                  {OPS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
                <input value={String(r.value)} onChange={e => {
                  let v = e.target.value;
                  if (/^-?\d+(\.\d+)?$/.test(v)) v = parseFloat(v);
                  else if (v === "true") v = true;
                  else if (v === "false") v = false;
                  setRule(i, { value: v });
                }} className="h-8 px-2 border border-slate-200 rounded text-xs flex-1" placeholder="value"/>
                <button onClick={() => delRule(i)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5"/></button>
              </div>
            ))}
            {(w.rules || []).length === 0 && <div className="text-xs text-slate-400">No rules — this is a catch-all template.</div>}
          </div>

          <div className="border border-slate-200 rounded-md p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Steps (ordered)</div>
              <button data-testid="wf-add-step" onClick={addStep} className="ml-auto text-xs text-[#005289] hover:underline">+ Add step</button>
            </div>
            {w.steps.map((s, i) => (
              <div key={i} className="flex gap-2 mb-2 items-center">
                <GripVertical className="w-4 h-4 text-slate-300"/>
                <span className="text-xs font-mono text-slate-500 w-6">#{i + 1}</span>
                <select value={s.role} onChange={e => setStep(i, { role: e.target.value })} className="h-8 px-2 border border-slate-200 rounded text-xs w-40">
                  {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
                </select>
                <input value={s.name} onChange={e => setStep(i, { name: e.target.value })} placeholder="Step name" className="h-8 px-2 border border-slate-200 rounded text-xs flex-1"/>
                <input type="number" value={s.sla_hours || ""} onChange={e => setStep(i, { sla_hours: parseInt(e.target.value) || null })} placeholder="SLA hrs" className="h-8 px-2 border border-slate-200 rounded text-xs w-24"/>
                <button onClick={() => delStep(i)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5"/></button>
              </div>
            ))}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="h-9 px-4 border border-slate-200 rounded-md text-sm">Cancel</button>
          <button data-testid="wf-save-btn" onClick={onSave} className="h-9 px-4 rounded-md bg-[#005289] text-white text-sm font-semibold flex items-center gap-1"><Save className="w-4 h-4"/> Save</button>
        </div>
      </div>
    </div>
  );
}

const inp = "w-full h-9 px-3 border border-slate-200 rounded-md text-sm";
function Field({ label, children }) {
  return <div><label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">{label}</label>{children}</div>;
}
